import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import * as videosApi from '../../api/channel/videos';
import { uploadMultipart } from '../../api/upload';
import { axiosMutationWrapper, axiosQueryWrapper } from '../utils/axios-wrapper';
import { GFX_QUERY_KEYS } from './gfx';

const ITEMS_PER_PAGE = 20;

export const VIDEO_QUERY_KEYS = {
	fetchVideos: (channelId, mode, itemsPerPage, currentPage) => ['api', 'channel', 'videos', 'fetch', { channelId, mode, itemsPerPage, currentPage }],
	fetchVideosBase: () => ['api', 'channel', 'videos', 'fetch'],
	getDownloadLink: (videoId) => ['api', 'channel', 'videos', 'download', videoId],
};

export const useFetchVideos = (channelId, mode, itemsPerPage, currentPage) => useQuery(
	VIDEO_QUERY_KEYS.fetchVideos(channelId, mode, itemsPerPage, currentPage),
	axiosQueryWrapper(videosApi.fetchVideos, channelId, mode, itemsPerPage, currentPage),
);

export const useInfinityFetchVideos = (
	channelId,
	mode,
	itemsPerPage = ITEMS_PER_PAGE,
) => useInfiniteQuery(
	VIDEO_QUERY_KEYS.fetchVideos(channelId, mode, itemsPerPage),
	({ pageParam = 1 }) => axiosQueryWrapper(
		videosApi.fetchVideos,
		channelId,
		mode,
		itemsPerPage,
		pageParam,
	)(),
	{
		getNextPageParam: (lastPage, allPages) => {
			const currentItemsLenght = allPages.reduce((acc, curr) => acc + curr.data.length, 0);
			if (currentItemsLenght < lastPage.totalItemsCount) {
				return allPages.length + 1;
			}
			return undefined;
		},
	},
);

export const useUploadVideo = () => {
	const queryClient = useQueryClient();

	return useMutation(async ({
		channelId,
		videoFile,
		onUploadProgress,
		uploadOnMux = true,
	}) => {
		const { blob } = videoFile;
		const filename = await uploadMultipart(blob, onUploadProgress);
		const video = {
			isVod: videoFile?.isVod || false,
			label: videoFile.label,
			vodDescription: videoFile?.isVod ? videoFile?.vodDescription : undefined,
			vodPrice: videoFile?.vodPrice || 0,
			areCommentsDisabled: videoFile?.areCommentsDisabled || false,
			mimeType: blob.type,
			filename,
			fileSize: blob.size,
			isGfx: videoFile.isGfx || false,
			isShared: videoFile.isShared || undefined,
			thumbnailTimeCode: videoFile.thumbnailTimeCode,
			uploader: videoFile.uploader,
			audienceType: videoFile.audienceType,
			currencies: videoFile.currencies,
			layerConfig: videoFile.layerConfig,
		};

		const videoCreated = await axiosMutationWrapper(videosApi.createVideo)({ channelId, video });

		return videoCreated;
	}, {
		onSuccess: () => {
			queryClient.invalidateQueries(VIDEO_QUERY_KEYS.fetchVideosBase());
			queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
		},
	});
};

export const useUpdateVideo = () => {
	const queryClient = useQueryClient();

	return useMutation(async (videoData) => {
		const video = {
			_id: videoData._id,
			isVod: videoData.isVod,
			label: videoData.label,
			vodDescription: videoData.vodDescription,
			vodPrice: videoData.vodPrice,
			areCommentsDisabled: videoData.areCommentsDisabled,
			audienceType: videoData.audienceType,
			currencies: videoData.currencies,
			thumbnailTimeCode: videoData.thumbnailTimeCode,
			layerConfig: videoData.layerConfig,
		};

		return axiosMutationWrapper(videosApi.updateVideo)(video);
	}, {
		onSuccess: () => {
			queryClient.invalidateQueries(VIDEO_QUERY_KEYS.fetchVideosBase());
			queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
		},
	});
};

export const useDeleteVideo = () => {
	const queryClient = useQueryClient();

	return useMutation(
		axiosMutationWrapper(videosApi.deleteVideo),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(VIDEO_QUERY_KEYS.fetchVideosBase());
				queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
			},
		},
	);
};

export const useCloneVideo = () => {
	const queryClient = useQueryClient();

	return useMutation(async (video) => {
		const videoData = {
			clip: video.clip,
			isVod: video.isVod || false,
			label: video.label,
			vodDescription: video.isVod && video.vodDescription ? video.vodDescription : '',
			vodPrice: video.isVod && video.vodPrice ? video.vodPrice : 0,
			areCommentsDisabled: video.isVod && video.areCommentsDisabled
				? video.areCommentsDisabled : false,
			thumbnailTimeCode: video.thumbnailTimeCode,
		};
		const data = await axiosMutationWrapper(videosApi.cloneVideo)({
			cloneFromVideoId: video.cloneFrom,
			videoData,
		});

		return data;
	}, {
		onSuccess: () => {
			queryClient.invalidateQueries(VIDEO_QUERY_KEYS.fetchVideosBase());
			queryClient.invalidateQueries(GFX_QUERY_KEYS.fetchGfx());
		},
	});
};

// TODO: might not be working properly
export const useAddRecord = () => {
	const queryClient = useQueryClient();

	return useCallback((channelId, mode, record) => {
		queryClient.setQueryData(
			VIDEO_QUERY_KEYS.fetchVideos(channelId, mode),
			(videos) => (
				{
					data: [...(videos?.data || []), record],
					totalItemsCount: videos?.totalItemsCount,
				}
			),
		);
	}, [queryClient]);
};

export const useUpdateRecord = () => {
	const queryClient = useQueryClient();

	return useCallback((channelId, mode, record, itemsPerPage,
		currentPage) => {
		queryClient.setQueryData(
			VIDEO_QUERY_KEYS.fetchVideos(channelId, mode, itemsPerPage, currentPage),
			(videos) => (
				{
					data: videos?.data?.map((video) => (video._id === record._id ? record : video)),
					totalItemsCount: videos?.totalItemsCount,
				}
			),
		);
	}, [queryClient]);
};

export const useRevalidateVideos = () => {
	const queryClient = useQueryClient();
	return useCallback((channelId, mode, itemsPerPage = ITEMS_PER_PAGE) => {
		queryClient.invalidateQueries(VIDEO_QUERY_KEYS.fetchVideos(channelId, mode, itemsPerPage));
	}, [queryClient]);
};

export const useLikeVideo = () => useMutation(
	axiosMutationWrapper(videosApi.likeVideo),
);
